<template>
  <CCard>
    <CCardHeader>
      <div class="d-flex align-items-center">
        <CIcon
          name="cil-dollar"
          class="mr-2"
        />
        <strong>Account Billing</strong>

        <CButton
          color="info"
          class="ml-auto"
          size="sm"
          :to="{
            name: 'BuyerPaymentsList',
            query: { accountId: accountId }
          }"
          target="_blank"
        >
          View Pending Invoices
          <CIcon
            name="cil-external-link"
            size="custom"
            class="ml-1"
          />
        </CButton>

        <CButton
          color="info"
          class="ml-2"
          size="sm"
          :to="`/dashboard/accounts/buyer/${accountId}/payments`"
          target="_blank"
        >
          View Payment History

          <CIcon
            name="cil-external-link"
            size="custom"
            class="ml-1"
          />
        </CButton>
      </div>
    </CCardHeader>

    <CCardBody>
      <CForm
        novalidate
        @submit.prevent
      >
        <dl class="row">
          <dt class="col-sm-3 font-weight-normal">
            Current Balance
          </dt>
          <dd class="col-sm-9 d-flex align-items-center mb-0">
            <h6 class="mr-3">
              {{ getSafe(() => formatAmount(accountBalance.cashBalance)) }}
            </h6>

            <router-link
              v-if="isFinancialManager || isSuperAdmin"
              :to="{ name: 'BuyerAccountAddFunds', params: { accountId: accountId} }"
            >
              <CButton
                size="sm"
                color="success"
                style="position: relative; bottom: 3px;"
              >
                Add Funds
              </CButton>
            </router-link>

            <CButton
              v-if="isFinancialManager || isSuperAdmin"
              size="sm"
              color="warning"
              class="ml-2"
              style="position: relative; bottom: 3px;"
              :disabled="isGeneratingCollectionStatement"
              @click="generateCollectionStatementHandler"
            >
              <CSpinner
                v-if="isGeneratingCollectionStatement"
                size="sm"
                class="mr-1"
              />
              Collection Statement
            </CButton>

            <CButton
              v-if="isFinancialManager || isSuperAdmin"
              size="sm"
              color="info"
              class="ml-2"
              style="position: relative; bottom: 3px;"
              @click="showInvoicePreviewModal"
            >
              Preview Invoice
            </CButton>

            <CButton
              v-if="isFinancialManager || isSuperAdmin"
              size="sm"
              color="primary"
              class="ml-2"
              style="position: relative; bottom: 3px;"
              :disabled="isGeneratingInvoice"
              @click="generateInvoice"
            >
              <CSpinner
                v-if="isGeneratingInvoice"
                size="sm"
                class="mr-1"
              />
              Generate Invoice
            </CButton>
          </dd>
        </dl>

        <hr>

        <div class="form-group form-row">
          <label class="col-form-label col-sm-3">
            Is Pre Pay Buyer? 
            <CIcon 
              v-c-tooltip="'Pre Pay: pay upfront with credit card. Post Pay: use credit line, pay via invoice according to the payment terms. Switch from Post Pay to Pre Pay is not possible.'" 
              name="cil-lightbulb" 
              size="custom"
            />
          </label>

          <CSwitch
            label-off="No"
            label-on="Yes"
            shape="pill"
            color="primary"
            data-cy="pre-pay-checkbox"
            horizontal
            add-label-classes=""
            add-input-classes="w-25"
            size="lg"
            :disabled="!accountBillingConfig.prePay" 
            :checked.sync="accountBillingConfigLocal.prePay"
          />
        </div>
        
        <div 
          v-if="!accountBillingConfigLocal.prePay && accountBillingConfigLocal.prePaySwitchStatus !== null"
          class="form-group form-row"
        >
          <label class="col-form-label col-sm-3">
            Switch to Post-Paid:
            <CIcon 
              v-c-tooltip="'Switch to Post-Paid Buyer might take some time. If it is still in progress or completed/failed, you will see the status here'" 
              name="cil-lightbulb" 
              size="custom"
            />
          </label>
          <h5>
            <CBadge
              :color="getBadge(accountBillingConfigLocal.prePaySwitchStatus).color"
              shape="pill" 
            >
              {{ getBadge(accountBillingConfigLocal.prePaySwitchStatus).text }}
            </CBadge>
          </h5>
        </div>

        <CInput
          v-if="accountBillingConfigLocal.prePay"
          v-model="$v.accountBillingConfigLocal.minimumInitialDeposit.$model"
          type="number"
          label="Minimum Initial Deposit"
          name="minimumInitialDeposit"
          placeholder="Minimum Initial Deposit"
          description="amount in USD"
          :is-valid="checkIfValid('minimumInitialDeposit')"
          horizontal
          :invalid-feedback="minimumInitialDepositInvalid"
        />       

        <CInput
          v-if="accountBillingConfigLocal.prePay"
          v-model="$v.accountBillingConfigLocal.minimumBalance.$model"
          type="number"
          label="Minimum Balance"
          name="minimumBalance"
          placeholder="Balance"
          description="amount in USD"
          :is-valid="checkIfValid('minimumBalance')"
          horizontal
          :invalid-feedback="minimumBalanceInvalid"
        />
        
        <CInput
          v-if="!accountBillingConfigLocal.prePay"
          v-model="negatedMinimumBalance"
          type="number"
          label="Credit Line"
          name="creditLine"
          placeholder="Credit line"
          description="amount in USD"
          :is-valid="negatedMinimumBalance >= 0 && negatedMinimumBalance < max_credit_line"
          horizontal
          :invalid-feedback="creditLineInvalid"
        />
        
        <div
          v-if="accountBillingConfigLocal.prePay 
            && !accountBillingConfigLocal.autoRecharge 
            && accountBillingConfigLocal.minimumBalance < initial_minimum_balance"
        >
          <p class="alert alert-warning mt-2">
            <CIcon          
              name="cil-warning"
              class="text-danger mb-2"
            />
            <strong> Note:</strong>
            Minimum balance is less than suggested ${{ initial_minimum_balance }} (auto recharge is turned off)
          </p>
        </div>

        <div
          v-if="accountBillingConfigLocal.prePay 
            && accountBillingConfigLocal.autoRecharge 
            && accountBillingConfigLocal.minimumBalance < 0"
        >
          <p class="alert alert-warning mt-2">
            <CIcon          
              name="cil-warning"
              class="text-danger mb-2"
            />
            <strong> Note:</strong>
            Negative minimum balance for Pre Pay Buyer means giving them a credit
          </p>
        </div>

        <div
          v-if="!accountBillingConfigLocal.prePay           
            && negatedMinimumBalance < initial_credit_line"
        >
          <p class="alert alert-warning mt-2">
            <CIcon          
              name="cil-warning"
              class="text-danger mb-2"
            />
            <strong> Note:</strong>
            Credit line is less than suggested ${{ initial_credit_line }}
          </p>
        </div>

        <CInput          
          v-model="$v.accountBillingConfigLocal.balanceTooLowWarningAmount.$model"
          name="balanceTooLowWarningAmount"
          type="number"
          label="Balance Too Low Warning Amount"
          placeholder="Balance Too Low Warning Amount"          
          :description="balanceTooLowWarningAmountDescription"
          horizontal
          :is-valid="checkIfValid('balanceTooLowWarningAmount')"
          :invalid-feedback="balanceTooLowWarningAmountInvalid"
        />

        <CInput
          v-if="accountBillingConfigLocal.prePay"
          v-model="$v.accountBillingConfigLocal.minimumCreditCardChargeAmount.$model"
          name="minimumCreditCardChargeAmount"
          type="number"
          label="Minimum Credit Card Charge Amount"
          placeholder="Minimum Credit Card Charge Amount"
          description="amount in USD"
          horizontal
          :is-valid="checkIfValid('minimumCreditCardChargeAmount')"
          :invalid-feedback="minimumCreditCardChargeInvalid"
        />

        <CInput
          v-if="accountBillingConfigLocal.prePay"
          v-model="$v.accountBillingConfigLocal.maximumCreditCardChargeAmount.$model"
          type="number"
          label="Maximum Credit Card Charge Amount"
          name="maximumCreditCardChargeAmount"
          placeholder="Maximum Credit Card Charge Amount"
          description="amount in USD"
          :is-valid="checkIfValid('maximumCreditCardChargeAmount')"
          horizontal
          :invalid-feedback="maximumCreditCardChargeInvalid"
        />

        <div
          v-if="accountBillingConfigLocal.prePay"
          class="form-group form-row"
        >
          <label class="col-form-label col-sm-3">
            Auto Recharge
          </label>

          <CInputCheckbox
            data-cy="auto-recharge-checkbox"
            label="Enable"
            :checked.sync="accountBillingConfigLocal.autoRecharge"
            horizontal
            class="mt-2 ml-1"
          />
        </div>        

        <CInput
          v-if="accountBillingConfigLocal.prePay"
          v-model="$v.accountBillingConfigLocal.rechargeBalance.$model"
          type="number"
          label="Recharge Balance"
          name="rechargeBalance"
          placeholder="Balance"
          description="amount in USD"
          :is-valid="checkIfValid('rechargeBalance')"
          :readonly="!accountBillingConfigLocal.autoRecharge"
          horizontal
          :invalid-feedback="rechargeBalanceInvalid"
        />

        <CInput
          v-if="accountBillingConfigLocal.prePay"
          v-model="$v.accountBillingConfigLocal.rechargeAmount.$model"
          name="rechargeAmount"
          type="number"
          label="Recharge Amount"
          placeholder="Recharge Amount"
          description="amount in USD"
          horizontal
          :is-valid="checkIfValid('rechargeAmount')"
          :invalid-feedback="autoRechargeInvalid"
        />

        <CSelect
          v-if="!accountBillingConfigLocal.prePay"
          :value.sync="$v.accountBillingConfigLocal.billingCycle.$model"
          label="Billing Period"
          horizontal
          :options="billingCycles"
          placeholder="Please select"
          :is-valid="checkIfValid('billingCycle')"
          :invalid-feedback="billingCycleInvalid"
        />

        <CInput
          v-if="!accountBillingConfigLocal.prePay"
          :value.sync="accountBillingConfigLocal.paymentTermsInDays"
          type="number"
          label="Payment Terms"
          name="paymentTerms"
          placeholder="Payment terms (in days)"
          horizontal
          :is-valid="checkIfValid('paymentTermsInDays')"
          :invalid-feedback="paymentTermsInDaysInvalid"
        />

        <CInput
          v-if="!accountBillingConfigLocal.prePay"
          :value.sync="accountBillingConfigLocal.billingEmailsString"
          type="text"
          label="Billing Emails"
          name="billingEmails"
          description="Semicolon separated email addresses to send billing emails to. For example, john.doe@mail.com;alice.brown@mail.com"
          horizontal 
          :is-valid="checkIfValid('billingEmails')"         
        />

        <div
          v-if="false"
          class="form-group form-row"
        >
          <label class="col-form-label col-sm-3">
            Custom Dispute Fees
          </label>

          <div>
            <CSwitch
              label-off="No"
              label-on="Yes"
              shape="pill"
              color="primary"
              data-cy="pre-pay-checkbox"
              horizontal
              add-label-classes=""
              add-input-classes="w-25"
              size="lg"
              :checked.sync="accountBillingConfigLocal.setCustomDisputeFees"
              @update:checked="onChangeDisputeFeesSetting"
            />
            <div class="text-muted">
              Standard Dispute Fees of <strong>{{ formatAmount(globalBuyerDisputeFees) }}</strong> applies
            </div>
          </div>
        </div>

        <CInput
          v-if="accountBillingConfigLocal.setCustomDisputeFees"
          :value.sync="$v.accountBillingConfigLocal.disputeFees.$model"
          type="number"
          label="Dispute Fees"
          name="Dispute Fees"
          placeholder="Enter fees"
          :horizontal="{ label: 'col-sm-3', input: 'col-sm-4 col-12'}"
          :is-valid="checkIfValid('disputeFees')"
          invalid-feedback="Please enter dispute fees greater than $0"
        />

        <div class="form-row">
          <CCol
            :md="{ offset: '3', size: '9' }"
            :sm="{ offset: '3', size: '9' }"
          >
            <CButton
              type="submit"
              size="sm"
              color="primary"
              data-cy="submit-billing"
              @click="onFormSubmit"
            >
              Change
            </CButton>
          </CCol>
        </div>
      </CForm>
    </CCardBody>

    <CModal
      title="Confirm change billing"
      color="success"
      data-cy="change-billing-modal"
      :show.sync="warningBillingChangeModal"
    >
      Are you sure you want to edit the billing configuration?

      <p 
        v-if="!accountBillingConfigLocal.prePay && accountBillingConfig.prePay"
        class="alert alert-warning mt-2"
      >
        <CIcon          
          name="cil-warning"
          class="text-danger mb-2"
        />
        <strong> Note:</strong>
        Changing buyer to post-paid is irreversible action.
        If you switch to post-paid buyer, you will not be able to switch back to pre-paid buyer.
      </p>

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="isLoading"
          @click="onConfirmSubmit"
        >
          Change
        </ButtonLoading>

        <CButton
          color="danger"
          @click="warningBillingChangeModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>

    <InvoicePreviewModal
      :show.sync="invoicePreviewModal"
      :account-id="accountId"
    />

    <!-- Collection Statement Modal -->
    <CModal
      title="Collection Statement"
      color="info"
      size="xl"
      :show.sync="collectionStatementModal"
    >
      <div v-if="collectionStatementData">
        <div class="mb-3">
          <h5>{{ collectionStatementData.accountName }}</h5>
          <p class="mb-1">
            <strong>Statement ID:</strong> {{ collectionStatementData.statementId }}
          </p>
          <p class="mb-1">
            <strong>Generated:</strong> {{ formatDate(collectionStatementData.generatedAt) }}
          </p>
          <p class="mb-3">
            <strong>Total Outstanding:</strong> <span class="text-danger font-weight-bold">{{ formatAmount(collectionStatementData.totalOutstanding) }}</span>
          </p>
        </div>

        <h6 class="mb-3">
          Unpaid Invoices ({{ collectionStatementData.invoiceCount }})
        </h6>

        <CDataTable
          :items="collectionStatementData.invoices || []"
          :fields="[
            { key: 'invoiceId', label: 'Invoice ID' },
            { key: 'invoiceDate', label: 'Invoice Date' },
            { key: 'dueDate', label: 'Due Date' },
            { key: 'amountDue', label: 'Amount Due' },
            { key: 'disputeFees', label: 'Dispute Fees' },
            { key: 'amountPaid', label: 'Amount Paid' },
            { key: 'remainingBalance', label: 'Balance' },
            { key: 'daysOverdue', label: 'Days Overdue' }
          ]"
          hover
          striped
          border
          small
        >
          <template #invoiceDate="{item}">
            <td>{{ formatDate(item.invoiceDate) }}</td>
          </template>
          <template #dueDate="{item}">
            <td>{{ formatDate(item.dueDate) }}</td>
          </template>
          <template #amountDue="{item}">
            <td>{{ formatAmount(item.amountDue) }}</td>
          </template>
          <template #disputeFees="{item}">
            <td>{{ formatAmount(item.disputeFees) }}</td>
          </template>
          <template #amountPaid="{item}">
            <td>{{ formatAmount(item.amountPaid) }}</td>
          </template>
          <template #remainingBalance="{item}">
            <td class="font-weight-bold text-danger">
              {{ formatAmount(item.remainingBalance) }}
            </td>
          </template>
          <template #daysOverdue="{item}">
            <td :class="item.daysOverdue > 0 ? 'text-danger font-weight-bold' : ''">
              {{ item.daysOverdue }}
            </td>
          </template>
        </CDataTable>
      </div>

      <template #footer>
        <CButton
          color="primary"
          :disabled="!collectionStatementPdfUrl || loadingCollectionStatementPdf"
          @click="openCollectionStatementPdf"
        >
          <CSpinner
            v-if="loadingCollectionStatementPdf"
            size="sm"
            class="mr-1"
          />
          {{ loadingCollectionStatementPdf ? 'Generating PDF...' : 'Open PDF' }}
        </CButton>
        <CButton
          color="secondary"
          @click="collectionStatementModal = false"
        >
          Close
        </CButton>
      </template>
    </CModal>
  </CCard>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { minValue, required, maxValue  } from 'vuelidate/lib/validators'
import moment from 'moment'
import { formatAmount, formatDate } from '@/utils'

import { showSuccessMessage, showErrorMessage } from '@/notification-utils'
import InvoicePreviewModal from '@/components/InvoicePreviewModal.vue'
import {
  MIN_INITIAL_DEPOSIT,
  INITIAL_MINIMUM_BALANCE,  
  MIN_AUTO_RECHARGE,
  MIN_CREDIT_CARD_CHARGE_AMOUNT,
  MAX_CREDIT_CARD_CHARGE_AMOUNT,
  MIN_RECHARGE_BALANCE,
  INITIAL_CREDIT_LINE
} from '@/store/modules/registration-requests.store'
import {
  MIN_BUYER_ACCOUNT_BALANCE_RANGE,
  ACCOUNT_BILLING_DEFAULTS
} from '@/store/modules/accounts.store'

export default {
  name: 'AccountBillingDetails',

  components: {
    InvoicePreviewModal
  },

  props: {
    accountBillingConfig: {
      type: Object,
      default() {
        return {}
      }
    },

    accountId: {
      type: String,
      default: ''
    }
  },

  validations() {
    return {
      accountBillingConfigLocal: {
        minimumInitialDeposit: {
          minValue: minValue(MIN_INITIAL_DEPOSIT),
          required
        },

        minimumBalance: {
          minValue: minValue(MIN_BUYER_ACCOUNT_BALANCE_RANGE.min),
          maxValue: maxValue(MIN_BUYER_ACCOUNT_BALANCE_RANGE.max),
          required
        }, 
        
        balanceTooLowWarningAmount: {
          minValue: minValue(0),
          required
        },

        minimumCreditCardChargeAmount: {
          minValue: minValue(MIN_CREDIT_CARD_CHARGE_AMOUNT),
          required
        },        

        maximumCreditCardChargeAmount: {
          minValue: minValue(MIN_CREDIT_CARD_CHARGE_AMOUNT),
          maxValue: maxValue(MAX_CREDIT_CARD_CHARGE_AMOUNT),
          required
        },

        rechargeBalance: {
          minValue: this.computeMinRechargeBalance,
          required
        },

        rechargeAmount: {
          minValue: minValue(MIN_AUTO_RECHARGE),
          required
        },

        billingCycle: {
          required
        },

        paymentTermsInDays: {
          minValue: 14,
          required
        },

        disputeFees: {
          minValue: minValue(0)
        },

        billingEmails: {          
        }
      }
    }
  },

  data() {
    return {
      isLoading: false,
      isGeneratingInvoice: false,
      isGeneratingCollectionStatement: false,
      initial_minimum_balance: INITIAL_MINIMUM_BALANCE,
      initial_credit_line: INITIAL_CREDIT_LINE,
      max_credit_line: -MIN_BUYER_ACCOUNT_BALANCE_RANGE.min,

      warningBillingChangeModal: false,
      invoicePreviewModal: false,

      collectionStatementModal: false,
      collectionStatementData: null,
      collectionStatementId: null,
      collectionStatementPdfUrl: null,
      loadingCollectionStatementPdf: false,

      accountBillingConfigLocal: {
        rechargeBalance: 0,
        rechargeAmount: 0,
        minimumInitialDeposit: 0,
        minimumBalance: INITIAL_MINIMUM_BALANCE,
        balanceTooLowWarningAmount: 0,
        minimumCreditCardChargeAmount: 0,
        maximumCreditCardChargeAmount: 0,
        prePay: true,
        billingCycle: 0,
        paymentTermsInDays: ACCOUNT_BILLING_DEFAULTS.paymentTermsInDays,
        disputeFees: null,
        billingEmails: [],
        billingEmailsString: '',
        balanceTooLowWarningAmountDescription: ''
      },

      setCustomDisputeFees: false,
      minimumInitialDepositInvalid: `Please enter a valid value, it should not be below $${MIN_INITIAL_DEPOSIT}`,
      minimumCreditCardChargeInvalid: `Please enter a valid value, it should not be below $${MIN_CREDIT_CARD_CHARGE_AMOUNT}`,
      maximumCreditCardChargeInvalid: `Please enter a valid value, it should not be below $${MIN_CREDIT_CARD_CHARGE_AMOUNT} and above $${MAX_CREDIT_CARD_CHARGE_AMOUNT}`,      
      autoRechargeInvalid: `Please enter a valid value, it should not be below $${MIN_AUTO_RECHARGE}`,
      rechargeBalanceInvalid: ``,
      paymentTermsInDaysInvalid: `Please enter a valid value, it should be between 14 and 90`,
      billingCycleInvalid: `Please select a valid value from the list`,
      minimumBalanceInvalid: `Please enter a valid value, it should be between ${formatAmount(MIN_BUYER_ACCOUNT_BALANCE_RANGE.min)} - ${formatAmount(MIN_BUYER_ACCOUNT_BALANCE_RANGE.max)}`,
      creditLineInvalid: `Please enter a valid value, it should be between $0 - ${formatAmount(-MIN_BUYER_ACCOUNT_BALANCE_RANGE.min)}`,
      balanceTooLowWarningAmountInvalid: `Please enter a valid value, it should not be below $0`
    }
  },

  computed: {
    ...mapGetters('auth', [
      'isFinancialManager',
      'isSuperAdmin'
    ]),

    ...mapState('accounts', {
      accountBalance: 'accountBalance',
      globalBuyerDisputeFees: 'globalBuyerDisputeFees'
    }),

    ...mapState(['billingCycles']),

    isValid() {
      return !this.$v.$invalid
    },

    computeMinRechargeBalance() {
      if (!this.accountBillingConfigLocal.autoRecharge) {
        return 0
      }

      return MIN_RECHARGE_BALANCE > this.accountBillingConfigLocal.minimumBalance 
        ? minValue(MIN_RECHARGE_BALANCE)
        : minValue(this.accountBillingConfigLocal.minimumBalance)
    },

    negatedMinimumBalance: {
      get() {
        return -this.accountBillingConfigLocal.minimumBalance
      },
      set(value) {
        this.accountBillingConfigLocal.minimumBalance = -value
      }
    }
  },

  watch: {
    accountBillingConfig: {
      immediate: true,
      handler(newValue) {
        this.accountBillingConfigLocal = newValue
          ? { ...newValue, setCustomDisputeFees: newValue?.disputeFees != null, billingEmailsString: newValue?.billingEmails?.join(';') }
          : {
              rechargeBalance: 0,
              rechargeAmount: 0,
              minimumInitialDeposit: 0,
              minimumBalance: 0,
              balanceTooLowWarningAmount: 0,
              minimumCreditCardChargeAmount: 0,
              maximumCreditCardChargeAmount: 0,
              billingCycle: 0,
              paymentTermsInDays: ACCOUNT_BILLING_DEFAULTS.paymentTermsInDays,
              setCustomDisputeFees: false,
              billingEmails: [],
              billingEmailsString: ''
            }
      }
    },
    accountBillingConfigLocal: {
      immediate: true,
      deep: true,
      handler(newValue) {
        this.rechargeBalanceInvalid = `Please enter a valid value, it should not be below $${Math.max(newValue.minimumBalance, MIN_RECHARGE_BALANCE)}`          
        this.balanceTooLowWarningAmountDescription = `amount in USD. This is to warn Buyer when they have $${this.accountBillingConfigLocal.balanceTooLowWarningAmount} or less Balance left before reaching Minimum Balance. `
      }
    },

  },

  async created () {
    await this.getGlobalBuyerDisputeFees()

    await this.getAccountBalance(this.accountId)

    this.accountBillingConfigLocal.setCustomDisputeFees = this.accountBillingConfigLocal?.disputeFees != null
    this.accountBillingConfigLocal.billingEmailsString = this.accountBillingConfigLocal.billingEmails?.join(';')
  },

  methods: {
    ...mapActions('accounts', [
      'updateBuyerAccountBillingConfiguration', 
      'getGlobalBuyerDisputeFees'
    ]),

    ...mapActions('accounts', [
      'getAccountBalance'
    ]),

    ...mapActions('billing', [
      'previewInvoice',
      'closeBillingCycle',
      'generateCollectionStatement',
      'generateDownloadLink'
    ]),

    async generateCollectionStatementHandler() {
      this.isGeneratingCollectionStatement = true

      try {
        const response = await this.generateCollectionStatement({
          accountId: this.accountId
        })

        // Check if there are any unpaid invoices
        if (!response?.invoices || response.invoices.length === 0) {
          this.$notify({
            title: 'No Unpaid Invoices',
            text: 'This account has no outstanding invoices. All invoices have been paid.',
            type: 'info'
          })
          return
        }

        showSuccessMessage('Collection statement generated.')

        // Store the response data
        this.collectionStatementData = response
        this.collectionStatementId = response?.statementId
        this.collectionStatementPdfUrl = null

        // Show the modal with invoice line items
        this.collectionStatementModal = true

        // Start polling for PDF in background
        this.pollForCollectionStatementPdf()

      } catch (error) {
        this.$notify({
          title: 'Error',
          text: `Failed to generate collection statement: ${error.response?.data?.error || error.message || 'Unknown error'}`,
          type: 'error'
        })
      } finally {
        this.isGeneratingCollectionStatement = false
      }
    },

    async pollForCollectionStatementPdf() {
      if (!this.collectionStatementId) {
        return
      }

      this.loadingCollectionStatementPdf = true

      let downloadURL = null
      let attempts = 0
      const maxAttempts = 5
      const delay = 2000 // 2 seconds

      while (!downloadURL && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, delay))
        attempts++
        try {
          const linkResponse = await this.generateDownloadLink({
            accountId: this.accountId,
            invoiceId: this.collectionStatementId
          })
          if (linkResponse) {
            downloadURL = linkResponse
          }
        } catch (e) {
          if (e.response && e.response.status !== 404) {
            console.error('Error fetching download link:', e)
          }
        }
      }

      this.loadingCollectionStatementPdf = false

      if (downloadURL) {
        this.collectionStatementPdfUrl = downloadURL
      } else {
        showErrorMessage('PDF generation is taking longer than expected. Please try again later.')
      }
    },

    openCollectionStatementPdf() {
      if (this.collectionStatementPdfUrl) {
        window.open(this.collectionStatementPdfUrl, '_blank')
      }
    },

    async generateInvoice() {
      if (!confirm('Are you sure you want to generate an invoice for this account? This will close the current billing cycle and create an invoice.')) {
        return
      }

      this.isGeneratingInvoice = true

      try {
        // Use current UTC time minus 1 second to ensure it's in the past
        const endDate = moment().utc().subtract(1, 'second').toISOString()
        const result = await this.closeBillingCycle({
          accountId: this.accountId,
          endDate: endDate
        })

        this.$notify({
          title: 'Success',
          text: `Invoice generated successfully. Invoice ID: ${result.invoiceId}`,
          type: 'success'
        })

        // Refresh account balance
        await this.getAccountBalance(this.accountId)
      } catch (error) {
        this.$notify({
          title: 'Error',
          text: `Failed to generate invoice: ${error.response?.data?.error || error.message || 'Unknown error'}`,
          type: 'error'
        })
      } finally {
        this.isGeneratingInvoice = false
      }
    },

    showInvoicePreviewModal() {
      this.invoicePreviewModal = true
    },

    onFormSubmit() {
      this.$v.accountBillingConfigLocal.$touch()

      if (this.isValid) {
        this.warningBillingChangeModal = true
      }
    },

    async onConfirmSubmit() {
      if (this.isValid) {
        this.isLoading = true

        this.transformBillingEmailsFromLocal()

        try {
          await this.updateBuyerAccountBillingConfiguration({
            accountId: this.accountId,
            billingConfig: this.accountBillingConfigLocal
          })

          this.warningBillingChangeModal = false
          
          showSuccessMessage('Billing configuration updated successfully')

          this.$emit('prepay-status-changed')
        } catch (error) {
          // Handle error
        } finally {
          this.isLoading = false
        }
      }
    },

    transformBillingEmailsFromLocal() {
      if (this.accountBillingConfigLocal.billingEmailsString) {     
      this.accountBillingConfigLocal.billingEmails = this.accountBillingConfigLocal.billingEmailsString.split(';')
        .map(x => x.trim()).filter(x => x !== "")   
      } 
    },

    checkIfValid(fieldName) {
      const field = this.$v.accountBillingConfigLocal[fieldName]

      if (!field.$dirty) {
        return null
      }

      return !(field.$invalid || field.$model === '')
    },

    formatAmount,

    onChangeDisputeFeesSetting() {
      if (this.accountBillingConfigLocal.setCustomDisputeFees) {
        this.accountBillingConfigLocal.disputeFees = this.globalBuyerDisputeFees
      } else {
        this.accountBillingConfigLocal.disputeFees = null
      }
    },

    getBadge(status) {
      const statusToBadgeMap = {
        0: { color: 'danger', text: 'Error' },
        1: { color: 'info', text: 'In Progress' },
        2: { color: 'success', text: 'Completed' },
      }

      return statusToBadgeMap[status] || { color: 'secondary', text: 'Unknown' }
    },

    formatDate(date) {
      return formatDate(date)
    }
    
  }
}
</script>

<style></style>
